import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, SimpleChanges } from '@angular/core';
import * as dayjs from 'dayjs';
import * as FileSaver from 'file-saver';
import { EMPTY, Subject } from 'rxjs';
import { catchError, first, switchMap } from 'rxjs/operators';
import { capitalize } from 'src/app/static-data/models/consts/static-data.utils';
import { WorkflowMessageContext } from 'src/app/workflows/workflow-message-handler/workflow-handler.const';
import { WorkflowMessageHandler } from 'src/app/workflows/workflow-message-handler/workflow-message-handler';
import { DataTableColumns } from '../../model/interfaces/data-table-columns.interface';
import { AnalyticsService } from '../../services/analytics.service';
import { DocService } from '../../services/doc.service';
import { getDocErrorMessage } from '../overview/documents-tab/getDocErrorMessage';
import { GovernanceReport } from './governance-checks.consts';
import { GovernanceChecksService } from './governance-checks.service';

@Component({
  selector: 'app-governance-checks',
  templateUrl: './governance-checks.component.html',
  styleUrls: ['./governance-checks.component.scss'],
  providers: [GovernanceChecksService],
})
export class GovernanceChecksComponent extends WorkflowMessageHandler {
  loading = true;

  @Input()
  active: boolean;

  @Input()
  isin: string;

  @Input()
  targetMarkets: any;

  reportId: number;

  selectedTest: GovernanceReport[];

  results: GovernanceReport[] = [];

  testExecutionColumns: DataTableColumns[] = [
    {
      name: 'Date',
      prop: 'createdAt',
      resizeable: false,
      draggable: false,
      flexGrow: 1,
      cellTemplate: 'dateTimeTemplate',
    },
    {
      name: 'Result',
      prop: 'overallResult',
      resizeable: false,
      draggable: false,
      flexGrow: 1,
    },
  ];

  messages = {
    emptyMessage: '',
  };

  destroy$ = new Subject<void>();

  private governanceChecksService = inject(GovernanceChecksService);
  private docService = inject(DocService);
  private analyticsService = inject(AnalyticsService);

  ngOnChanges(changes: SimpleChanges): void {
    if ('targetMarkets' in changes) {
      changes.targetMarkets.currentValue && this.loadResults();
    }
    if ('active' in changes) {
      changes.active.currentValue && this.loadResults();
    }
  }

  runTests(): void {
    this.loading = true;
    const errorMessages = [];

    this.analyticsService.fetchAnalytics(this.productId.toString()).subscribe(
      (reports) => {
        if (!reports || reports.length === 0) {
          errorMessages.push(
            'No analytics reports available for this product.'
          );
        }

        if (!this.targetMarkets || this.targetMarkets.length === 0) {
          errorMessages.push(
            'At least one pair of client type and distribution strategy is required in the target markets.'
          );
        }

        if (errorMessages.length > 0) {
          const errorMessage = errorMessages.join('<br/>');

          this.dcAlertInnerService.showAlert({
            type: 'danger',
            message: errorMessage,
          });
          this.loading = false;
          return;
        }

        this.governanceChecksService
          .runTests(this.productId)
          .pipe(
            first(),
            catchError((error: HttpErrorResponse) => {
              this.dcAlertInnerService.showAlert({
                type: 'danger',
                message: getDocErrorMessage(error),
              });
              this.loading = false;
              return EMPTY;
            })
          )
          .subscribe(() => {
            this.loadResults(true);
          });
      },
      (error) => {
        this.dcAlertInnerService.showAlert({
          type: 'danger',
          message: 'Failed to fetch analytics reports.',
        });
        this.loading = false;
      }
    );
  }

  loadResults(selectLast = true): void {
    this.loading = true;

    this.governanceChecksService
      .getTestResults(this.productId)
      .pipe(
        first(),
        catchError((error: HttpErrorResponse) => {
          this.dcAlertInnerService.showAlert({
            type: 'danger',
            message: getDocErrorMessage(error),
          });

          this.loading = false;
          return EMPTY;
        })
      )
      .subscribe((reports: GovernanceReport[]) => {
        if (reports?.length) {
          const report = reports[!selectLast ? 0 : reports.length - 1];
          this.reportId = report.id;
          this.results = reports;
          this.onRowClicked([report]);
          this.loading = false;
        } else {
          this.loading = false;
        }
      });
  }

  onRowClicked(row: any): void {
    const item = row[0];

    item.source =
      item.source.indexOf('_') > -1
        ? capitalize(item.source, '_')
        : item.source;

    this.selectedTest = row;
    this.reportId = item.id;
  }

  downloadReport(): void {
    this.docService
      .downloadGovernanceChecksDocument(this.productId, this.reportId)
      .pipe(
        first(),
        catchError((error: HttpErrorResponse) => {
          this.dcAlertInnerService.showAlert({
            type: 'danger',
            message: getDocErrorMessage(error),
          });

          return EMPTY;
        }),
        switchMap(({ document }: { document: string }) => {
          const filename = `governance-report_${
            this.isin?.length ? `${this.isin}_` : ''
          }${dayjs().format('YYYY-MM-DD')}.pdf`;
          return this.docService.base64ToFile(document, filename);
        })
      )
      .subscribe(FileSaver.saveAs);
  }

  refreshData(): void {
    this.loadResults();
  }

  get context(): WorkflowMessageContext {
    return WorkflowMessageContext.GOVERNANCE_REPORT;
  }

}
